/**
 * A failed load must never render as "empty" or "all clear".
 *
 * The library ServiceStore SWALLOWS a failed `find` by default: it records the
 * rejection on `errorOnFind` and resolves `null` (`throwOnError` defaults to
 * false — `@intrasync/stores` ServiceStore `shouldThrowOnError`). A view store
 * that wraps `await service.find()` in try/catch therefore never reaches its
 * catch, and the page shows "No clients match the current filters", "No roles
 * yet" or a green "0.0% error rate" while its data is in fact unavailable —
 * the systemic defect behind most frames of the walkthrough 5×5 audit
 * (plan/2026-09-28_walkthrough-5x5-audit.md §3.1 / §6.2).
 *
 * `findOrThrow` asks for the failure explicitly (per-call `throwOnError`, the
 * same mechanism ClientsStore's save path uses) and ALSO treats a null result
 * that carries a recorded `errorOnFind` as a failure, so the caller's existing
 * catch finally does its job. A null result with no recorded error (a find
 * superseded by a newer one) is returned as-is: that is not a failure.
 */

import { isSessionEnded } from "../stores/sessionGuard";

type FindableService = {
  find: (params?: Record<string, unknown>) => Promise<unknown>;
  errorOnFind?: { message?: unknown } | null;
};

/** The slots a library ServiceStore parks a swallowed rejection on. */
export type SwallowedKey =
  | "errorOnFind"
  | "errorOnGet"
  | "errorOnCreate"
  | "errorOnPatch"
  | "errorOnUpdate"
  | "errorOnRemove";

/** What a ServiceStore recorded on `key` when it swallowed a rejection, as-is. */
function swallowedRecord(store: unknown, key: SwallowedKey): unknown {
  return (store as Record<string, unknown> | null | undefined)?.[key] ?? null;
}

/** The message the library recorded on `key` for a swallowed call, if any. */
export function swallowedMessage(store: unknown, key: SwallowedKey): string | null {
  const err = swallowedRecord(store, key) as { message?: unknown } | null;
  return typeof err?.message === "string" && err.message ? err.message : null;
}

/** The message the library recorded for a swallowed `find`, if any. */
export function swallowedFindMessage(store: unknown): string | null {
  return swallowedMessage(store, "errorOnFind");
}

/**
 * True when the call the ServiceStore swallowed on `key` was ended by a
 * sign-out. Check it before writing anything on a null-result branch: the
 * store was reset under the call, and "Signed out before … loaded." is not a
 * failure of the next person's.
 */
export function swallowedSessionEnded(store: unknown, key: SwallowedKey = "errorOnFind"): boolean {
  return isSessionEnded(swallowedRecord(store, key));
}

/**
 * The error to THROW for a call the ServiceStore swallowed (a null/undefined
 * result with the reason on `errorOn<Method>`). A sign-out's
 * `SessionEndedError` is returned as-is, so the catch below still recognises
 * it with `isSessionEnded`; `new Error(swallowedFindMessage(...))` stripped its
 * name and code and the catch then wrote "Signed out before … loaded." onto a
 * store the sign-out had just reset. Anything else becomes an Error with the
 * recorded message, or `fallback` when nothing was recorded.
 *
 *     if (result == null) throw swallowedFailure(this.service, "errorOnFind", "Failed to load");
 */
export function swallowedFailure(store: unknown, key: SwallowedKey, fallback: string): Error {
  const recorded = swallowedRecord(store, key);
  if (isSessionEnded(recorded)) return recorded;
  return new Error(swallowedMessage(store, key) ?? fallback);
}

/** Thrown when a page's data service is not registered at all. */
export const SERVICE_UNAVAILABLE_MESSAGE =
  "This page's data service is not available.";

/**
 * `service.find(params)` that REJECTS on failure instead of resolving null.
 * Use it anywhere a store would otherwise turn a failed load into an empty list.
 */
export async function findOrThrow<T = unknown>(
  service: FindableService | null | undefined,
  params: Record<string, unknown> = {}
): Promise<T> {
  if (!service) throw new Error(SERVICE_UNAVAILABLE_MESSAGE);
  const result = await service.find({ ...params, throwOnError: true });
  // Check the recorded error whatever came back, not only on null: an
  // offline-enabled store (`@intrasync/stores` OfflineServiceStore) CATCHES the
  // rejection our throwOnError asked for and answers from its offline cache —
  // a non-null, usually empty, result — while `errorOnFind` still holds the
  // failure. That fallback made Roles read "No roles yet." on a failed load.
  // `findInternal` clears errorOnFind when a find starts, so an error present
  // now belongs to this call (or one racing it), never to an older one.
  const swallowed = swallowedFindMessage(service);
  if (swallowed) {
    // A call the sign-out ended keeps its identity, so the loader's catch can
    // tell "this person signed out" from "the load failed".
    const recordedError = service.errorOnFind;
    if (isSessionEnded(recordedError)) throw recordedError;
    throw new Error(swallowed);
  }
  return result as T;
}

type GettableService = {
  get: (id: string, params?: Record<string, unknown>) => Promise<unknown>;
  errorOnGet?: unknown;
};

/**
 * `service.get(id, params)` that REJECTS on failure instead of resolving
 * undefined — `findOrThrow` for a single record. Without it a failed get reads
 * as "not found". The recorded error is rethrown as-is (not re-wrapped), so a
 * Feathers NotFound keeps its 404 and `isNotFoundError` can still tell "this
 * record does not exist" from "this record could not be loaded".
 * `getInternal` clears `errorOnGet` when a get starts, so an error present
 * afterwards belongs to this call.
 */
export async function getOrThrow<T = unknown>(
  service: GettableService | null | undefined,
  id: string,
  params: Record<string, unknown> = {}
): Promise<T | undefined> {
  if (!service) throw new Error(SERVICE_UNAVAILABLE_MESSAGE);
  const result = await service.get(id, { ...params, throwOnError: true });
  const recorded = service.errorOnGet;
  if (recorded) {
    throw recorded instanceof Error
      ? recorded
      : Object.assign(new Error(loadErrorMessage(recorded)), recorded);
  }
  return (result ?? undefined) as T | undefined;
}

/** A Feathers NotFound (404) — the record does not exist, as opposed to a failed fetch. */
export function isNotFoundError(err: unknown): boolean {
  const e = err as { code?: unknown; name?: unknown; className?: unknown } | null;
  return e?.code === 404 || e?.name === "NotFound" || e?.className === "not-found";
}

/** A human message for a caught load failure. */
export function loadErrorMessage(
  err: unknown,
  fallback = "This data could not be loaded."
): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string" && err) return err;
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? message : fallback;
}

/**
 * {@link loadErrorMessage}, except that a call the sign-out ended
 * (`SessionEnded`) yields `null` — "no error": the store was reset under it and
 * whoever signs in next must not be shown "Signed out before … loaded." as a
 * load failure of their own. Write this, not `loadErrorMessage`, into an error
 * field that is `string | null`; a catch that writes anything else derived from
 * the error checks `isSessionEnded(err)` first (stores/sessionGuard.ts).
 */
export function loadErrorOrNull(
  err: unknown,
  fallback = "This data could not be loaded."
): string | null {
  return isSessionEnded(err) ? null : loadErrorMessage(err, fallback);
}
